import React, { useEffect } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import { ActionErrorEntry } from '../decom-policy-utils.tsx';
import { FieldErrors, UseFormWatch } from 'react-hook-form';
import { DecomPolicyFormValues } from '../decom-policy-form-layout.tsx';

import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { useTheme } from '@mui/material/styles';
import WarningAmberOutlined from '@mui/icons-material/WarningAmberOutlined';

interface ActionAccordionOldProps {
  id: string;
  title: React.ReactNode;
  description?: React.ReactNode;
  icon?: React.ReactNode;
  expanded: boolean;
  onToggleExpand: (isExpanded: boolean) => void;
  onRemove?: () => void;
  children?: React.ReactNode;
  errors: FieldErrors<DecomPolicyFormValues>;
  watch: UseFormWatch<DecomPolicyFormValues>;
  actionTypesWithErrors: ActionErrorEntry[];
  day: number;
}

export default function ActionAccordionOld({
  id,
  title,
  description,
  icon,
  expanded,
  onToggleExpand,
  onRemove,
  children,
  errors,
  actionTypesWithErrors,
  watch,
  day,
}: ActionAccordionOldProps) {
  const theme = useTheme();
  const actionsData = watch(`actions.${day}.actions`) ?? [];

  const matchingIndex = actionsData.findIndex((a) => a.actionType === id);

  const matchingIndexInitErrors = actionTypesWithErrors.findIndex(
    (a) => a.id === id
  );

  const actionErrors = errors?.actions?.[day]?.actions?.[matchingIndex]
    ? errors.actions?.[day]?.actions?.[matchingIndex]
    : [];
  const actionInitErrors =
    actionTypesWithErrors && matchingIndexInitErrors !== -1
      ? actionTypesWithErrors[matchingIndexInitErrors]?.errors
      : null;
  const hasErrors =
    Boolean(actionErrors && Object.keys(actionErrors).length) &&
    Boolean(actionInitErrors && Object.keys(actionInitErrors).length);

  useEffect(() => {
    if (hasErrors) {
      onToggleExpand(true);

      let firstId: string | undefined;

      if (actionTypesWithErrors.length > 0) {
        firstId = actionTypesWithErrors[actionTypesWithErrors.length - 1].id;
      } else if (actionErrors && typeof actionErrors === 'object') {
        const firstKey = Object.keys(
          actionErrors
        )[0] as keyof typeof actionErrors;
        const errorVal = actionErrors[firstKey] as
          | Record<string, unknown>
          | undefined;
        const refName = (errorVal?.ref as { name?: string } | undefined)?.name;

        if (refName) {
          const match = refName.match(/actions\.(\d+)/);
          if (match) {
            const idx = parseInt(match[1]);
            const actionType = watch(
              `actions.${day}.actions.${idx}.actionType`
            );
            if (actionType) firstId = actionType;
          }
        }
      }

      if (firstId) {
        setTimeout(() => {
          const element = document.getElementById(firstId);
          element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionErrors]);

  return (
    <Accordion
      id={id}
      expanded={expanded}
      onChange={(_, isExpanded) => onToggleExpand(isExpanded)}
      disableGutters
      sx={{
        p: icon ? 0 : '16px 0px 0px 16px',
        mb: 2,
        width: '100%',
        borderRadius: 0,
        overflow: 'hidden',
        boxShadow: `0px 1px 1px 0px rgba(0,0,0,0.14),
                    0px 2px 1px -1px rgba(0,0,0,0.2),
                    0px 1px 3px 0px rgba(0,0,0,0.12)`,
        position: 'relative',
        '&:before': { display: 'none' },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          padding: 1,
          userSelect: 'none',
          minHeight: 64,
          gap: 1,
          cursor: 'pointer',
        }}
        onClick={() => onToggleExpand(!expanded)}
      >
        {icon && (
          <Box
            onClick={() => onToggleExpand(!expanded)}
            sx={{
              width: 32,
              height: 32,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              cursor: 'pointer',
            }}
          >
            {icon}
          </Box>
        )}

        <Box flex="1">
          <Typography
            onClick={() => onToggleExpand(!expanded)}
            sx={{
              fontWeight: 500,
              fontSize: theme.typography.body1.fontSize,
              color: theme.palette.text.primary,
              cursor: 'pointer',
            }}
          >
            <Box
              component="span"
              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
            >
              {title}{' '}
              {title &&
              ((actionInitErrors && Object.keys(actionInitErrors).length > 0) ||
                (actionErrors && Object.keys(actionErrors).length > 0)) ? (
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.palette.warning.main,
                    ml: 2,
                    display: 'inline-flex',
                    alignItems: 'center',
                    textAlign: 'center',
                  }}
                >
                  <WarningAmberOutlined
                    sx={{ width: '16px', height: '18px', mr: 0.5 }}
                  />{' '}
                  Needs Attention
                </Typography>
              ) : null}
            </Box>
          </Typography>
          {description && (
            <Typography
              sx={{
                fontSize: theme.typography.body2.fontSize,
                color: theme.palette.text.secondary,
                mt: 0.25,
              }}
            >
              {description}
            </Typography>
          )}
        </Box>

        <Box display="flex" alignItems="center">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpand(!expanded);
            }}
            aria-label={expanded ? 'Collapse' : 'Expand'}
          >
            <ExpandMoreIcon
              sx={{
                transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: theme.transitions.create('transform', {
                  duration: theme.transitions.duration.shortest,
                }),
              }}
            />
          </IconButton>

          {onRemove && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onRemove();
              }}
              aria-label="Remove"
            >
              <CloseIcon />
            </IconButton>
          )}
        </Box>
      </Box>

      <AccordionSummary
        expandIcon={null}
        sx={{
          height: 0,
          minHeight: 0,
          padding: 0,
          margin: 0,
          visibility: 'hidden',
        }}
      />

      <AccordionDetails sx={{ pt: 0 }}>{children}</AccordionDetails>
    </Accordion>
  );
}
